import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchNearby, fetchPOI } from '@services/api'
import { getPOIsOffline, savePOIContent, getPOIContent, addBookmark, removeBookmark, getBookmarks, getSetting } from '@services/indexeddb'
import { speakAnnouncement } from '@services/tts'
import POIDetails from './POIDetails'

type Item = { id: string; name: string; lat: number; lon: number; distance_m: number; city?: string; country?: string; wiki_title?: string; interesting_fact?: string }

export default function NearbyList({ lang }: { lang: string }) {
  const [items, setItems] = useState<Item[]>([])
  const lastSpoken = useRef<Map<string, number>>(new Map())
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<any | null>(null)
  const [bookmarks, setBookmarks] = useState<any[]>([])
  const [fake, setFake] = useState<{ enabled: boolean; lat: number; lon: number }>({ enabled: false, lat: 0, lon: 0 })
  useEffect(() => {
    let stopped = false
    getBookmarks().then(setBookmarks)
    const readSettings = async () => {
      const [en, la, lo] = await Promise.all([getSetting('fake_location_enabled'), getSetting('fake_lat'), getSetting('fake_lon')])
      const f = { enabled: !!en, lat: Number(la || 0), lon: Number(lo || 0) }
      setFake(f)
      return f
    }
    const loadWith = async (lat: number, lon: number) => {
      try {
        let data: Item[] = []
        if (navigator.onLine) {
          const res = await fetchNearby(lat, lon, 50000, lang, 20)
          data = res.items
          if (!data || data.length === 0) {
            data = await getPOIsOffline(lat, lon, 50000)
          }
        } else {
          data = await getPOIsOffline(lat, lon, 50000)
        }
        if (stopped) return
        setItems(data)
        if (data && data.length > 0) {
          const first = data[0]
          window.dispatchEvent(new CustomEvent('focus-changed', { detail: { city: first.city, country: first.country } }))
        }
        const near = data[0]
        if (near && near.distance_m <= 100 && near.id) {
          const last = lastSpoken.current.get(near.id) || 0
          const now = Date.now()
            if (now - last > 180000) {
              lastSpoken.current.set(near.id, now)
              let details = await getPOIContent(near.id, lang)
              if (navigator.onLine && (!details || (details.summary?.lang && details.summary.lang !== lang))) {
                try {
                  const fetched = await fetchPOI(near.id, lang)
                  if (fetched) {
                    details = fetched
                    await savePOIContent(near.id, lang, details)
                  }
                } catch { }
              }
              // Prefer the Wikipedia extract (already in selected language);
              // fall back to interesting_fact then bare name so voice is never silent
              const extract: string = details?.summary?.extract || ''
              const fact: string = details?.interesting_fact || ''
              const spokenText = extract
                ? `${near.name}. ${extract.split('.')[0]}.`
                : `${near.name}. ${fact}`
              await speakAnnouncement(spokenText, lang)
            }
        }
      } catch {
        if (!stopped) setItems([])
      }
    }
    readSettings().then(f => {
      if (f.enabled) loadWith(f.lat, f.lon)
      else {
        try {
          navigator.geolocation.getCurrentPosition(p => loadWith(p.coords.latitude, p.coords.longitude), () => {
            loadWith(-33.9249, 18.4241)
          }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 })
        } catch {
          loadWith(-33.9249, 18.4241)
        }
      }
    })
    const onDevLoc = async () => {
      const f = await readSettings()
      if (f.enabled) loadWith(f.lat, f.lon)
    }
    window.addEventListener('devloc-changed', onDevLoc)
    const watchId = navigator.geolocation.watchPosition(async pos => {
      const f = await readSettings()
      const lat = f.enabled ? f.lat : pos.coords.latitude
      const lon = f.enabled ? f.lon : pos.coords.longitude
      loadWith(lat, lon)
    }, async () => {
      const f = await readSettings()
      if (f.enabled) loadWith(f.lat, f.lon)
      else loadWith(-33.9249, 18.4241)
    }, { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 })
    return () => {
      stopped = true
      window.removeEventListener('devloc-changed', onDevLoc)
      navigator.geolocation.clearWatch(watchId)
    }
  }, [lang])
  return (
    <div className="p-4 md:p-8 h-full overflow-y-auto no-scrollbar flex flex-col gap-6">
      <header>
        <h1 className="font-headline text-3xl font-extrabold text-on-surface tracking-tight mb-2">Discover the City</h1>
        <p className="text-on-surface-variant">Handpicked experiences near your current location.</p>
      </header>
      <div className="space-y-6">
        {items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-on-surface-variant text-center gap-3">
            <span className="material-symbols-outlined text-5xl opacity-40">explore_off</span>
            <p className="font-semibold text-base">No places found nearby</p>
            <p className="text-sm opacity-70">Move closer to a landmark, expand your search radius, or use the fake location tool to test a Cape Town location.</p>
          </div>
        )}
        {items.map(it => {
          const isBookmarked = bookmarks.some(b => b.id === it.id)
          return (
            <div key={it.id} className="bg-surface-container-lowest rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow group">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-headline text-xl font-bold text-on-surface">{it.name}</h3>
                <button className={`transition-colors ${isBookmarked ? 'text-tertiary' : 'text-outline hover:text-tertiary'}`} onClick={async () => {
                  if (isBookmarked) {
                    await removeBookmark(it.id)
                  } else {
                    await addBookmark({ id: it.id, name: it.name, lat: it.lat, lon: it.lon, pinStatus: 'not_pinned' })
                  }
                  setBookmarks(await getBookmarks())
                }}>
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: isBookmarked ? "'FILL' 1" : "'FILL' 0" }}>bookmark</span>
                </button>
              </div>
              <p className="text-on-surface-variant text-sm line-clamp-2 mb-4 leading-relaxed font-medium">
                {Math.round(it.distance_m)}m away
              </p>
              <div className="flex gap-3">
                <button className="flex-1 bg-primary text-on-primary py-2.5 rounded-xl font-semibold text-sm hover:brightness-110 transition-all" onClick={async () => {
                  let d = await getPOIContent(it.id, lang)
                  if (navigator.onLine && (!d || (d.summary?.lang && d.summary.lang !== lang))) {
                    try {
                      const fetched = await fetchPOI(it.id, lang)
                      if (fetched) {
                        d = fetched
                        await savePOIContent(it.id, lang, d)
                      }
                    } catch { }
                  }
                  if (d) await savePOIContent(it.id, lang, d)
                  setSelected(d || { name: it.name })
                  setOpen(true)
                }}>Details</button>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${it.lat},${it.lon}`} target="_blank" rel="noreferrer" className="px-4 border border-outline-variant text-primary rounded-xl hover:bg-surface-container-high transition-all flex items-center justify-center">
                  <span className="material-symbols-outlined">directions</span>
                </a>
              </div>
            </div>
          )
        })}
      </div>
      <POIDetails open={open} onClose={() => setOpen(false)} data={selected} lang={lang} />
    </div>
  )
}
